import React from 'react';
import { FileText, ExternalLink, CreditCard, Calendar, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';

const InvoiceList = ({ invoices, onSelectInvoice, loading = false }) => {
  console.log("here is the loading", loading);
  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'bg-gray-100 text-black border border-gray-200', icon: CheckCircle, label: 'Paid' },
      pending: { color: 'bg-gray-100 text-black border border-gray-200', icon: Clock, label: 'Pending' },
      failed: { color: 'bg-gray-100 text-black border border-gray-200', icon: XCircle, label: 'Failed' },
      cancelled: { color: 'bg-gray-100 text-black border border-gray-200', icon: XCircle, label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getPlanBadge = (planKey) => {
    const planColors = {
      enterprise_key: 'bg-gray-100 text-black border-gray-200',
      professional_key: 'bg-gray-100 text-black border-gray-200',
      basic_key: 'bg-gray-100 text-black border-gray-200'
    };
    
    return planColors[planKey] || 'bg-gray-100 text-black border-gray-200';
  };

  const handleGoToBilling = () => {
    window.location.href = '/billing';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Invoice History</h2>
          <p className="mt-1 text-sm text-gray-500">View and download your past invoices</p>
        </div>
        
        <div className="p-12">
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 text-black animate-spin" />
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Loading invoices...</h3>
            <p className="mt-2 text-sm text-gray-500">
              Please wait while we fetch your invoice history.
            </p>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Details
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing Cycle
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              <tr>
                <td colSpan="6" className="px-6 py-8">
                  <div className="flex items-center justify-center space-x-2 text-gray-400">
                    <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
                  </div>
                </td>
              </tr>
              <tr>
                <td colSpan="6" className="px-6 py-8">
                  <div className="flex items-center justify-center space-x-2 text-gray-400">
                    <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  </div>
                </td>
              </tr>
              <tr>
                <td colSpan="6" className="px-6 py-8">
                  <div className="flex items-center justify-center space-x-2 text-gray-400">
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No invoices found</h3>
          <p className="mt-2 text-sm text-gray-500">
            You don't have any invoices yet. Subscribe to a plan to get started.
          </p>
          <div className="mt-6">
            <button
              onClick={handleGoToBilling}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Go to Billing
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Invoice History</h2>
        <p className="mt-1 text-sm text-gray-500">View and download your past invoices</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Invoice Details
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plan
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Billing Cycle
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <tr key={invoice._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">{invoice.invoice_number}</span>
                    <span className="text-xs text-gray-500 mt-1">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {new Date(invoice.invoice_date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                    {invoice.due_date && (
                      <span className="text-xs text-gray-400 mt-0.5">
                        Due: {new Date(invoice.due_date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${getPlanBadge(invoice.plan_key)}`}>
                      {invoice.plan_name}
                    </span>
                    {invoice.line_items && invoice.line_items[0]?.description && (
                      <span className="text-xs text-gray-400 mt-1">
                        {invoice.line_items[0].description}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-900 capitalize">
                    {invoice.billing_cycle || 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-900">
                      ${invoice.amount.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500">{invoice.currency}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(invoice.status)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    {invoice.url && (
                      <button
                        onClick={() => {
                          onSelectInvoice(invoice);
                          window.open(invoice.url, '_blank');
                        }}
                        className="inline-flex items-center text-sm text-black hover:text-gray-700 font-medium transition-colors"
                        title="View Invoice"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Invoice
                      </button>
                    )}
                    {invoice.sub_url && (
                      <button
                        onClick={() => {
                          window.open(invoice.sub_url, '_blank');
                        }}
                        className="inline-flex items-center text-sm text-black hover:text-gray-700 font-medium transition-colors"
                        title="View Subscription Details"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Subscription
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>Showing {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</span>
          <div className="flex items-center space-x-4">
            <span className="text-xs">Contact: {invoices[0]?.payer_contact || 'N/A'}</span>
            <span className="text-xs">Email: {invoices[0]?.payer_email || 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceList;